<!-- Header.vue -->
<template>
  <!-- backdrop for mobile menu -->
  <div v-if="isMenuOpen" class="fixed inset-0 bg-black/50 z-[60]" @click="closeMenu" />

  <header class="relative z-50">
    <!-- Top info bar (NOT sticky) -->
    <div ref="topBarRef" class="hidden md:block border-b border-gray-100 text-slate-600 text-[14px] bg-white">
      <div class="max-w-7xl mx-auto px-4 lg:px-6">
        <div class="flex items-center justify-between h-10">
          <div class="flex items-center gap-6">
            <a
              class="inline-flex items-center gap-2 hover:text-slate-800 transition"
              href="https://maps.google.com/?q=312 Falcon Tower Diplomatic Area, Manama, Kingdom of Bahrain"
              target="_blank"
              rel="noopener"
            >
              <span class="font-icon theme-icon location-icon text-slate-500 text-[16px]" />
              <span class="truncate">312 Falcon Tower Diplomatic Area, Manama, Kingdom of Bahrain</span>
            </a>

            <a class="inline-flex items-center gap-2 hover:text-slate-800 transition" href="tel:+97332141440">
              <font-awesome-icon size="sm" icon="fa-solid fa-mobile-screen" class="text-slate-500" />
              <span class="font-medium">+973 3214 1440</span>
            </a>
          </div>

          <div class="inline-flex items-center gap-2">
            <span class="font-icon theme-icon clock-icon text-slate-500 text-[16px]" />
            <span>24/7 Available</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Spacer so layout doesn’t jump when nav becomes fixed -->
    <div :style="{ height: isFixed ? navHeight + 'px' : '0px' }"></div>

    <!-- MAIN NAV -->
    <div
      ref="navWrapRef"
      :class="[
        isFixed ? 'fixed top-3 left-0 right-0 z-[55] pointer-events-none' : 'relative',
        'nav-transition'
      ]"
    >
      <div
        :class="[
          'relative',
          'max-w-7xl mx-auto px-4 lg:px-6',
          'pointer-events-auto card-transition',
          isFixed
            ? 'glass--frozen rounded-2xl ring-1 ring-white/60 shadow-xl translate-y-0 scale-[0.985] opacity-100'
            : 'translate-y-0 scale-100 opacity-100'
        ]"
      >
        <!-- frosted overlay for stronger “frozen glass” -->
        <div v-if="isFixed" class="glass-overlay--frozen" aria-hidden="true"></div>

        <div class="flex items-center h-20 relative z-10">
          <!-- Logo -->
          <button @click="go('Home')" class="shrink-0 focus:outline-none" aria-label="Go to home">
            <img src="/assets/logo.png" alt="Vehitra World" class="h-12 w-auto md:h-14" width="180" height="56" />
          </button>

          <!-- Desktop menu -->
          <nav class="hidden md:flex items-center mx-auto uppercase text-[15px] text-slate-700" aria-label="Primary">
            <ul class="flex items-center gap-8">
              <li v-for="item in baseItems" :key="item.id" class="relative group">
                <button
                  @click="handleTopNav(item)"
                  class="inline-flex items-center gap-1 tracking-wide hover:text-slate-900 focus-visible:outline-none"
                >
                  <span>{{ item.title }}</span>
                </button>
                <span class="absolute -bottom-2 left-0 h-[2px] w-0 bg-emerald-500 transition-all duration-300 group-hover:w-full" />
              </li>
            </ul>
          </nav>

          <!-- Right side -->
          <div class="hidden md:flex items-center gap-3 ml-auto">
            <!-- Logged OUT -->
            <template v-if="!auth">
              <button
                @click="go('Login')"
                class="px-4 py-2 text-[14px] font-medium rounded-xl border border-gray-200 hover:bg-gray-50"
              >
                Log in
              </button>

              <div class="relative">
                <button
                  @click.stop="toggleSignup"
                  class="px-4 py-2 text-[14px] font-medium rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1 transition-all"
                  aria-haspopup="menu"
                  :aria-expanded="expandedItemId === 6 ? 'true' : 'false'"
                >
                  <span>Signup</span>
                  <svg class="h-4 w-4 text-white/80" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M5.25 7.5l4.5 4.5 4.5-4.5h-9z" />
                  </svg>
                </button>

                <div
                  v-if="expandedItemId === 6"
                  class="absolute right-0 mt-2 min-w-[180px] bg-white border border-gray-100 rounded-xl shadow-lg py-2 z-[70]"
                  role="menu"
                >
                  <button class="block w-full text-left px-4 py-2 text-[14px] hover:bg-gray-50 border-b border-gray-50" @click="handleSignupRoute({ type: 'web' })">
                    User
                  </button>
                  <button class="block w-full text-left px-4 py-2 text-[14px] hover:bg-gray-50" @click="handleSignupRoute({ type: 'agent' })">
                    Agency
                  </button>
                </div>
              </div>
            </template>

            <!-- Logged IN -->
            <div v-else class="relative" @keydown.escape="openUserMenu = false">
              <button
                type="button"
                @click.stop="toggleUserMenu"
                class="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-2 text-[14px] font-medium hover:bg-gray-50"
                aria-haspopup="menu"
                :aria-expanded="openUserMenu ? 'true' : 'false'"
              >
                <div class="h-8 w-8 rounded-full bg-emerald-600 text-white flex items-center justify-center font-semibold">
                  {{ initials }}
                </div>
                <span>Hello, {{ displayName }}</span>
                <svg class="h-4 w-4 text-slate-500" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M5.25 7.5l4.5 4.5 4.5-4.5h-9z" />
                </svg>
              </button>

              <transition name="fade">
                <div
                  v-if="openUserMenu"
                  class="absolute right-0 mt-2 w-48 overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-black/5 z-[70]"
                  role="menu"
                >
                  <button v-if="auth?.user?.type !== 'web'" @click="nav('/account/dashboard')" class="block w-full px-4 py-2 text-left text-[14px] text-slate-700 hover:bg-gray-50">
                    Dashboard
                  </button>
                  <button @click="nav(`/${auth?.user?.type}/bookings`)" class="block w-full px-4 py-2 text-left text-[14px] text-slate-700 hover:bg-gray-50">
                    View orders
                  </button>
                  <div class="my-1 border-t border-slate-100"></div>
                  <button @click="handleLogout" class="block w-full px-4 py-2 text-left text-[14px] text-red-600 hover:bg-red-50">
                    Logout
                  </button>
                </div>
              </transition>
            </div>

            <div class="relative">
              <button
                @click="handleSignupRoute({ type: 'supplier' })"
                class="px-5 py-2 rounded-full bg-green-700 text-white text-[15px] font-semibold shadow hover:bg-green-800 transition flex items-center gap-1"
              >
                <span>Partner</span>
              </button>
            </div>
          </div>

          <!-- Mobile burger -->
          <button
            class="md:hidden ml-auto p-2 rounded-lg border border-gray-200 bg-white/70"
            @click="openMenu"
            aria-label="Open menu"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M3 6h18M3 12h18M3 18h18" stroke="#334155" stroke-width="2" stroke-linecap="round" />
            </svg>
          </button>
        </div>

        <!-- MOBILE MENU -->
        <Teleport to="body">
          <Transition name="slide-menu">
            <aside
              v-if="isMenuOpen"
              class="fixed top-0 right-0 h-full w-[86%] max-w-sm z-[70]
                     bg-white/85 supports-[backdrop-filter]:backdrop-blur-2xl
                     shadow-2xl ring-1 ring-black/5 rounded-l-2xl
                     flex flex-col overflow-hidden pointer-events-auto"
              role="dialog"
              aria-modal="true"
              aria-labelledby="mobileMenuTitle"
            >
              <div class="flex items-center justify-between px-5 py-4 border-b border-gray-200/70">
                <div class="text-slate-800 font-semibold tracking-wide uppercase text-[13px]" id="mobileMenuTitle">Menu</div>
                <button @click="closeMenu" class="p-2 rounded-lg bg-white/60 hover:bg-white transition" aria-label="Close menu">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M6 6l12 12M18 6L6 18" stroke="#0f172a" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                </button>
              </div>

              <nav aria-label="Mobile" class="flex-1 overflow-y-auto">
                <ul class="px-2 py-2">
                  <li v-for="item in mobileMenu" :key="item.id">
                    <template v-if="item.id !== 7">
                      <button
                        class="w-full text-left px-4 py-3 rounded-xl text-[15px] text-slate-800 hover:bg-white border border-transparent hover:border-gray-200 active:scale-[0.99] transition"
                        @click="handleMenuItemClick(item)"
                      >
                        {{ item.title }}
                      </button>
                    </template>
                    <template v-else>
                      <button
                        @click="toggleMobileSignup"
                        class="w-full flex items-center justify-between px-4 py-3 rounded-xl text-[15px] text-slate-800 hover:bg-white border border-transparent hover:border-gray-200 active:scale-[0.99] transition"
                      >
                        <span>Signup</span>
                        <font-awesome-icon :icon="showMobileSignup ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'" class="text-[12px] opacity-80" />
                      </button>
                      <div v-if="showMobileSignup" class="flex flex-col gap-1 px-4 pt-1 pb-2">
                        <button class="w-full text-left px-4 py-2 rounded-lg bg-gray-50 text-[14px] text-slate-800" @click="handleSignupRoute({ type: 'web' }); isMenuOpen = false">
                          User
                        </button>
                        <button class="w-full text-left px-4 py-2 rounded-lg bg-gray-50 text-[14px] text-slate-800" @click="handleSignupRoute({ type: 'agent' }); isMenuOpen = false">
                          Agency
                        </button>
                      </div>
                    </template>
                  </li>
                </ul>
              </nav>

              <div class="px-5 pb-6 pt-3 border-t border-gray-200/70 bg-white/60">
                <div class="flex flex-col gap-2">
                  <button
                    @click="handleSignupRoute({ type: 'supplier' }); isMenuOpen = false"
                    class="block w-full text-center px-5 py-3 rounded-full bg-green-700 text-white font-semibold shadow hover:bg-green-800 transition flex items-center justify-center gap-2"
                  >
                    <span>Partner</span>
                  </button>
                </div>

                <div class="flex items-center justify-center gap-4 mt-3 text-slate-500 text-[13px]">
                  <a href="tel:+97332141440" class="hover:text-slate-700">+973 3214 1440</a>
                  <span aria-hidden="true">•</span>
                  <span>24/7 Available</span>
                </div>
              </div>
            </aside>
          </Transition>
        </Teleport>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import router from '@/router'
import { isLoggedIn, logout } from '@/utils/common'

/* ===== REACTIVE AUTH ===== */
const auth = ref(isLoggedIn())
const refreshAuth = () => { auth.value = isLoggedIn() }

/* ===== STATE ===== */
const isMenuOpen = ref(false)
const expandedItemId = ref<number | null>(null)
const openUserMenu = ref(false)
const showMobilePartner = ref(false)

/* ===== NAV ITEMS ===== */
const baseItems = [
  { id: 1, title: 'Home', component: 'Home' },
  { id: 2, title: 'About us', component: 'AboutUs' },
  { id: 3, title: 'Our services', component: 'OurServices' },
  { id: 4, title: 'Contact us', component: 'ContactUs' },
  { id: 5, title: 'FAQ', component: 'FAQ' }
]
const mobileMenu = computed(() => ([
  ...baseItems,
  { id: 6, title: auth.value ? 'My dashboard' : 'Log in', component: auth.value ? 'AccountDashboard' : 'Login' },
  { id: 7, title: 'Signup', component: null }
]))

/* ===== GREETING ===== */
const displayName = computed(() => {
  const u: any = auth.value?.user
  const name = u?.first_name || u?.name || (u?.email ? u.email.split('@')[0] : '')
  return name ? name.charAt(0).toUpperCase() + name.slice(1) : 'User'
})
const initials = computed(() => {
  const u: any = auth.value?.user
  const first = (u?.first_name || u?.name || '').trim()
  const last  = (u?.last_name || '').trim()
  if (first) return (first[0] + (last ? last[0] : '')).toUpperCase()
  const email = u?.email || ''
  return email ? email[0].toUpperCase() : 'U'
})

/* ===== HELPERS ===== */
const go = (name: string) => router.push({ name })
const handleTopNav = (item: any) => { if (item.component) router.push({ name: item.component }) }
const handleSignupRoute = (child: any) => { expandedItemId.value = null; router.push({ name: 'Signup', params: { type: child.type } }) }
const handleMenuItemClick = (item: any) => {
  if (item.component) { router.push({ name: item.component }); isMenuOpen.value = false }
  else if (item.id === 7) { router.push({ name: 'Signup', params: { type: 'web' } }); isMenuOpen.value = false }
}
const toggleSignup = () => { expandedItemId.value = expandedItemId.value === 6 ? null : 6 }
const togglePartnerMenu = () => { expandedItemId.value = expandedItemId.value === 'partner' ? null : 'partner' }
const showMobileSignup = ref(false)
const toggleMobileSignup = () => { showMobileSignup.value = !showMobileSignup.value }
const toggleMobilePartner = () => { showMobilePartner.value = !showMobilePartner.value }
const toggleUserMenu = () => (openUserMenu.value = !openUserMenu.value)
const nav = (path: string) => { openUserMenu.value = false; router.push(path) }
const handleLogout = async () => {
  openUserMenu.value = false
  await logout(router)
  refreshAuth()
}

/* ===== MOBILE MENU ===== */
const openMenu = () => (isMenuOpen.value = true)
const closeMenu = () => { isMenuOpen.value = false; expandedItemId.value = null }

/* ===== STICKY NAV ===== */
const topBarRef = ref<HTMLElement | null>(null)
const navWrapRef = ref<HTMLElement | null>(null)
const isFixed = ref(false)
const navHeight = ref(80)
let observer: IntersectionObserver | null = null
const measure = () => { if (navWrapRef.value) navHeight.value = navWrapRef.value.getBoundingClientRect().height || 80 }

const closeAllMenus = () => {
  expandedItemId.value = null
  openUserMenu.value = false
}

onMounted(async () => {
  await nextTick()
  measure()
  observer = new IntersectionObserver(entries => { isFixed.value = !entries[0].isIntersecting }, { threshold: 0 })
  if (topBarRef.value) observer.observe(topBarRef.value)
  window.addEventListener('resize', measure, { passive: true })
  window.addEventListener('click', closeAllMenus)
})
onBeforeUnmount(() => {
  if (observer && topBarRef.value) observer.unobserve(topBarRef.value)
  window.removeEventListener('resize', measure)
  window.removeEventListener('click', closeAllMenus)
})
</script>

<style scoped>
.theme-icon { display: inline-block; line-height: 1; }

/* ===== FROZEN GLASS EFFECT ===== */
.glass--frozen {
  background: rgba(255, 255, 255, 0.86);
  backdrop-filter: blur(34px) saturate(145%) contrast(105%);
  -webkit-backdrop-filter: blur(34px) saturate(145%) contrast(105%);
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.55),
    0 20px 40px rgba(15, 23, 42, 0.12);
}

.glass-overlay--frozen {
  position: absolute;
  inset: 0;
  border-radius: 1rem;
  pointer-events: none;
  background:
    /* soft vertical frost */
    linear-gradient(180deg, rgba(255, 255, 255, 0.60), rgba(255, 255, 255, 0.20)),
    /* vignette highlight */
    radial-gradient(120% 100% at 50% 0%, rgba(255, 255, 255, 0.22), transparent 70%),
    /* very subtle vertical streaks */
    repeating-linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.06) 0px,
      rgba(255, 255, 255, 0.06) 1px,
      rgba(255, 255, 255, 0.00) 1px,
      rgba(255, 255, 255, 0.00) 6px
    ),
    /* tiny frost particles */
    radial-gradient(1px 1px at 10% 20%, rgba(255, 255, 255, 0.05), transparent 1px),
    radial-gradient(1px 1px at 70% 80%, rgba(255, 255, 255, 0.05), transparent 1px);
  background-blend-mode: screen, normal, overlay, normal, normal;
  -webkit-mask-image: radial-gradient(90% 120% at 50% 50%, rgba(0, 0, 0, 1), rgba(0, 0, 0, 0.85));
          mask-image: radial-gradient(90% 120% at 50% 50%, rgba(0, 0, 0, 1), rgba(0, 0, 0, 0.85));
}

/* ===== TRANSITIONS ===== */
.slide-menu-enter-from, .slide-menu-leave-to { transform: translateX(100%); opacity: 0; }
.slide-menu-enter-to, .slide-menu-leave-from { transform: translateX(0); opacity: 1; }
.slide-menu-enter-active, .slide-menu-leave-active {
  transition: transform 320ms cubic-bezier(0.22, 0.61, 0.36, 1), opacity 320ms cubic-bezier(0.22, 0.61, 0.36, 1);
}

.fade-enter-active, .fade-leave-active { transition: opacity .12s ease, transform .12s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; transform: translateY(-4px); }

/* perf-friendly hints */
.nav-transition, .card-transition { will-change: transform, opacity; }

@media (prefers-reduced-motion: reduce) {
  .slide-menu-enter-active, .slide-menu-leave-active,
  .fade-enter-active, .fade-leave-active { transition: none !important; }
}
</style>